/**
 * The Product feedback page's pure rules (SA-1305): its URL state, the timeline before the send, the
 * page link rebuilt from the report, and the environment facts. No React, no store.
 */
import type { AssessmentKind, ReportStatus } from '@/services/helpFeedback/appFeedbackApi'
import { browserLine, screenLine, tourLine } from '@/services/feedbackContext/feedbackContextText'
import { agoText } from '@/components/helpFeedback/helpFeedbackText'
import { whenText } from '@/components/process/stepLiveText'
import * as T from './feedbackAdminText'

export const KINDS: AssessmentKind[] = ['problem', 'idea', 'question', 'praise', 'guidance']
export const STATUSES: ReportStatus[] = ['new', 'triaged', 'planned', 'done', 'not_planned']

export interface Filters {
  kind?: AssessmentKind
  status?: ReportStatus
  user?: string
  pagePath?: string
  guidance: boolean
  /** Only the questions the help could not answer, grouped by page (SA-1314). */
  gaps: boolean
  page: number
  report?: string
}

const pick = <V extends string>(values: readonly V[], raw: string | null): V | undefined =>
  raw !== null && (values as readonly string[]).includes(raw) ? (raw as V) : undefined

export function readFilters(params: URLSearchParams): Filters {
  const page = Number(params.get('page'))
  return {
    kind: pick(KINDS, params.get('kind')),
    status: pick(STATUSES, params.get('status')),
    user: params.get('user') || undefined,
    pagePath: params.get('page_path') || undefined,
    guidance: params.get('guidance') === '1',
    gaps: params.get('gaps') === '1',
    page: Number.isInteger(page) && page > 1 ? page : 1,
    report: params.get('report') || undefined,
  }
}

export type FilterPatch = Partial<Omit<Filters, 'report'>>

const KEYS: Record<'kind' | 'status' | 'user' | 'pagePath', string> = {
  kind: 'kind', status: 'status', user: 'user', pagePath: 'page_path',
}

/** The next URL: any filter change drops `page`; the open report and unknown keys stay. */
export function writeFilters(params: URLSearchParams, patch: FilterPatch): URLSearchParams {
  const next = new URLSearchParams(params)
  let filterChanged = false
  for (const [field, key] of Object.entries(KEYS) as [keyof typeof KEYS, string][]) {
    if (!(field in patch)) continue
    filterChanged = true
    const value = patch[field]
    if (value) next.set(key, value)
    else next.delete(key)
  }
  for (const flag of ['guidance', 'gaps'] as const) {
    if (!(flag in patch)) continue
    filterChanged = true
    if (patch[flag]) next.set(flag, '1')
    else next.delete(flag)
  }
  if ('page' in patch && !filterChanged) {
    if (patch.page && patch.page > 1) next.set('page', String(patch.page))
    else next.delete('page')
  } else if (filterChanged) {
    next.delete('page')
  }
  return next
}

export const hasFilters = (f: Filters) => Boolean(f.kind || f.status || f.user || f.pagePath || f.guidance || f.gaps)

/** The rows in runs of one page, in the order the server sent them (it orders gaps by page). */
export function groupByPage<R extends { page_path: string | null }>(rows: R[]): { path: string | null; rows: R[] }[] {
  const out: { path: string | null; rows: R[] }[] = []
  for (const row of rows) {
    const last = out[out.length - 1]
    if (last && last.path === row.page_path) last.rows.push(row)
    else out.push({ path: row.page_path, rows: [row] })
  }
  return out
}

/** "just before", "42 s before", "3 min before", "2 h before". */
export function beforeWords(ms: number): string {
  const seconds = Math.floor(ms / 1000)
  if (seconds < 1) return 'just before'
  if (seconds < 60) return `${seconds} s before`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} min before`
  return `${Math.floor(minutes / 60)} h before`
}

export interface TimelineLine {
  key: string
  kind: string
  text: string
  detail: string | null
  tone: 'normal' | 'error'
  before: string | null
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined)
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined)
const joined = (...parts: (string | undefined)[]) => parts.filter(Boolean).join(' · ') || null

function line(e: Record<string, unknown>): Omit<TimelineLine, 'key' | 'before'> {
  const kind = str(e.kind) ?? ''
  if (kind === 'nav') {
    const path = str(e.path) ?? ''
    const title = str(e.title)
    return { kind, text: T.opened(title ?? path), detail: title ? path || null : null, tone: 'normal' }
  }
  if (kind === 'click') return { kind, text: T.pressed(str(e.name) ?? ''), detail: str(e.role) ?? null, tone: 'normal' }
  if (kind === 'tour') return { kind, text: tourLine(str(e.action) ?? '', str(e.tour) ?? '', num(e.step)), detail: null, tone: 'normal' }
  if (kind === 'change') return { kind, text: T.changed(str(e.field) ?? ''), detail: null, tone: 'normal' }
  if (kind === 'api') {
    const status = num(e.status) ?? 0
    const failed = status === 0 || status >= 400
    return {
      kind,
      text: T.requestLine(str(e.method) ?? '', str(e.path) ?? '', status, num(e.ms)),
      detail: failed ? str(e.code) ?? null : null,
      tone: failed ? 'error' : 'normal',
    }
  }
  if (kind === 'error') {
    return { kind, text: str(e.message) ?? str(e.code) ?? '', detail: joined(str(e.source), str(e.code)), tone: 'error' }
  }
  return { kind, text: kind || T.UNKNOWN_EVENT, detail: null, tone: 'normal' }
}

/** The context ring as lines, oldest first, each with its time before `sentAt`. Junk entries drop. */
export function timeline(events: unknown, sentAt: string | null): { lines: TimelineLine[]; errorCount: number } {
  const sent = sentAt ? Date.parse(sentAt) : NaN
  const rows = (Array.isArray(events) ? events : [])
    .filter((e): e is Record<string, unknown> => Boolean(e) && typeof e === 'object' && !Array.isArray(e))
    .map((e, i) => ({ e, i, at: num(e.at) }))
    .sort((a, b) => (a.at ?? 0) - (b.at ?? 0) || a.i - b.i)
  const lines = rows.map(({ e, i, at }) => ({
    key: `${i}`,
    ...line(e),
    before: at !== undefined && Number.isFinite(sent) ? beforeWords(sent - at) : null,
  }))
  return { lines, errorCount: lines.filter((l) => l.tone === 'error').length }
}

/** The person's URL rebuilt from the report's page: null unless every `:param` fills in-app. */
export function pageHref(page: Record<string, unknown> | null | undefined): string | null {
  const path = str(page?.path)
  if (!path || !path.startsWith('/') || path.startsWith('//')) return null
  const params = Array.isArray(page?.params) ? [...(page!.params as unknown[])] : []
  let unfilled = false
  const filled = path
    .split('/')
    .map((segment) => {
      if (!segment.startsWith(':')) return segment
      const value = params.shift()
      if (typeof value !== 'string' || !value) {
        unfilled = true
        return segment
      }
      return encodeURIComponent(value)
    })
    .join('/')
  if (unfilled) return null
  const query = page?.query && typeof page.query === 'object' ? (page.query as Record<string, unknown>) : {}
  const search = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) if (typeof v === 'string') search.set(k, v)
  const qs = search.toString()
  return qs ? `${filled}?${qs}` : filled
}

export interface EnvironmentFact {
  label: string
  value: string
}

/** The environment snapshot as label/value facts, only what it carries. */
export function environmentFacts(env: Record<string, unknown> | null | undefined): EnvironmentFact[] {
  if (!env) return []
  const facts: EnvironmentFact[] = []
  if (str(env.browser) || str(env.platform)) {
    facts.push({ label: T.ENV_BROWSER, value: browserLine(str(env.browser), num(env.browser_version), str(env.platform)) })
  }
  const w = num(env.viewport_w)
  const h = num(env.viewport_h)
  if (w !== undefined && h !== undefined) {
    facts.push({ label: T.ENV_WINDOW, value: screenLine(w, h, str(env.theme)).replace(/^Window /, '') })
  }
  if (str(env.build)) facts.push({ label: T.ENV_BUILD, value: str(env.build)! })
  if (Array.isArray(env.features) && env.features.length > 0) {
    facts.push({ label: T.ENV_FEATURES, value: env.features.filter((f) => typeof f === 'string').join(', ') })
  }
  if (env.view_as === true) facts.push({ label: T.ENV_VIEW_AS, value: T.ENV_VIEW_AS_VALUE })
  return facts
}

const DAY_MS = 24 * 60 * 60 * 1000

/** A sent time: relative within a day ("12 minutes ago"), else "16 Oct 14:02" (`whenText`). */
export function sentWords(iso: string | null | undefined, now: Date = new Date(), zone?: string): string {
  const at = iso ? Date.parse(iso) : NaN
  if (!Number.isFinite(at)) return T.DASH
  const ago = now.getTime() - at
  if (ago >= 0 && ago < DAY_MS) return agoText(ago)
  return whenText(iso, now, zone) ?? T.DASH
}

/** The full time for a tip: "Friday 16 October 2026, 14:02". */
export function fullTime(iso: string | null | undefined, zone?: string): string {
  const at = iso ? Date.parse(iso) : NaN
  if (!Number.isFinite(at)) return ''
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'full', timeStyle: 'short', timeZone: zone }).format(at)
}
